import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type {
  IndustryTemplate,
  PromptTemplate,
} from "@chat-yai/shared/admin-types";
import { promptsApi } from "@/api/endpoints/prompts";
import { promptTemplatesApi } from "@/api/endpoints/promptTemplates";
import { Modal } from "@/components/Modal";
import { Drawer } from "@/components/Drawer";
import { EmptyState } from "@/components/EmptyState";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { permissionTooltip } from "@/lib/rbac";

const PROMPT_MAX = 16 * 1024;

const promptSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(120, "Name too long."),
  content: z
    .string()
    .min(10, "Prompt is too short.")
    .max(PROMPT_MAX, "Prompt exceeds 16 KB."),
  is_active: z.boolean(),
});
type PromptValues = z.infer<typeof promptSchema>;

interface Props {
  chatbotId: string;
  canEdit: boolean;
}

export function ChatbotPromptsTab({ chatbotId, canEdit }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<PromptTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);

  const listQuery = useQuery({
    queryKey: ["prompts", chatbotId],
    queryFn: () => promptsApi.list({ chatbot_id: chatbotId, channel: "chat" }),
  });

  const templatesQuery = useQuery({
    queryKey: ["prompts", "templates"],
    queryFn: () => promptTemplatesApi.list(),
    enabled: templateDrawerOpen,
    staleTime: 5 * 60_000,
  });

  const form = useForm<PromptValues>({
    defaultValues: { name: "", content: "", is_active: false },
  });
  const contentLen = form.watch("content")?.length ?? 0;

  useEffect(() => {
    if (editing) {
      form.reset({
        name: editing.name,
        content: editing.content,
        is_active: editing.is_active,
      });
    } else if (creating) {
      // If the form was already prefilled (template flow) keep the values;
      // only reset to defaults when the modal opens "blank".
      const currentName = form.getValues("name");
      if (!currentName) {
        form.reset({
          name: "Custom prompt",
          content: listQuery.data?.default_content ?? "",
          is_active: true,
        });
      }
    }
    // form.reset is stable but exhaustive-deps wants form in the deps list;
    // the conditional guard above prevents the "infinite reset" trap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, creating, listQuery.data?.default_content]);

  const applyTemplate = (tpl: IndustryTemplate): void => {
    form.reset({
      name: tpl.name,
      content: tpl.content,
      is_active: true,
    });
    setTemplateDrawerOpen(false);
    setCreating(true);
  };

  const saveMutation = useMutation({
    mutationFn: async (v: PromptValues) => {
      const parsed = promptSchema.parse(v);
      if (editing) {
        return promptsApi.update(editing.id, parsed);
      }
      return promptsApi.create({
        chatbot_id: chatbotId,
        channel: "chat",
        name: parsed.name,
        content: parsed.content,
        is_active: parsed.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts", chatbotId] });
      setEditing(null);
      setCreating(false);
      toast.push("success", "Prompt saved.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const useDefault = useMutation<void, unknown, void>({
    mutationFn: async () => {
      // Deactivate any active prompt by activating none — backend then falls
      // back to the platform default text-chat prompt.
      const active = (listQuery.data?.items ?? []).find((p) => p.is_active);
      if (!active) return;
      await promptsApi.update(active.id, { is_active: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts", chatbotId] });
      toast.push("success", "Reverted to platform default prompt.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div style={{ marginTop: 16 }}>
      <div className="filters-bar" style={{ marginBottom: 12 }}>
        <div className="help" style={{ flex: 1, margin: 0 }}>
          Prompts shape the assistant&apos;s tone and rules. Only one prompt per
          channel can be active at a time.
        </div>
        {canEdit ? (
          <>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => useDefault.mutate()}
              disabled={useDefault.isPending}
              data-testid="use-default-prompt"
            >
              Use platform default
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setTemplateDrawerOpen(true)}
              data-testid="open-template-drawer"
            >
              <Icon name="doc" size={12} /> From template
            </button>
            <button
              className="btn btn-accent btn-sm"
              onClick={() => {
                form.reset({
                  name: "",
                  content: listQuery.data?.default_content ?? "",
                  is_active: true,
                });
                setCreating(true);
              }}
              data-testid="open-new-prompt"
            >
              <Icon name="plus" size={12} /> New prompt
            </button>
          </>
        ) : (
          <button className="btn btn-accent btn-sm" disabled title={permissionTooltip("write")}>
            <Icon name="plus" size={12} /> New prompt
          </button>
        )}
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "2fr 100px 100px 140px 120px" }}
        >
          <div>Name</div>
          <div>Active</div>
          <div>Version</div>
          <div>Updated</div>
          <div></div>
        </div>
        {items.map((p) => (
          <div
            key={p.id}
            className="list-row"
            style={{ gridTemplateColumns: "2fr 100px 100px 140px 120px" }}
            data-testid={`prompt-row-${p.id}`}
          >
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{p.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {p.content.slice(0, 80)}
                {p.content.length > 80 ? "…" : ""}
              </div>
            </div>
            <div>
              {p.is_active ? (
                <span className="chip chip-success">Active</span>
              ) : (
                <span className="chip">Draft</span>
              )}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              v{p.version}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(p.updated_at).toLocaleDateString()}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {canEdit ? (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setEditing(p)}
                  data-testid={`edit-prompt-${p.id}`}
                >
                  Edit
                </button>
              ) : (
                <button className="btn btn-outline btn-sm" disabled title={permissionTooltip("write")}>
                  View
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 ? (
          <EmptyState
            icon="doc"
            title="No custom prompts yet"
            description="The platform's default front-desk prompt is in use. Pick an industry template or write your own to make the chatbot sound like your business."
            primary={
              canEdit
                ? {
                    label: "From template",
                    onClick: () => setTemplateDrawerOpen(true),
                    testId: "empty-prompts-template",
                  }
                : undefined
            }
            secondary={
              canEdit
                ? {
                    label: "New prompt",
                    onClick: () => {
                      form.reset({
                        name: "",
                        content: listQuery.data?.default_content ?? "",
                        is_active: true,
                      });
                      setCreating(true);
                    },
                    testId: "empty-prompts-new",
                  }
                : undefined
            }
            testId="empty-prompts"
          />
        ) : null}
      </div>

      <Drawer
        title="Industry templates"
        open={templateDrawerOpen}
        onClose={() => setTemplateDrawerOpen(false)}
        width={460}
      >
        <p className="help" style={{ marginTop: 0 }}>
          Eight starter prompts written for common New Zealand small-business
          industries. Select one to prefill the new-prompt form — you can edit
          freely before saving.
        </p>
        {templatesQuery.isLoading ? (
          <div className="empty">Loading templates…</div>
        ) : (
          <div className="template-drawer-list">
            {(templatesQuery.data?.templates ?? []).map((tpl) => (
              <button
                key={tpl.slug}
                type="button"
                className="template-card"
                onClick={() => applyTemplate(tpl)}
                data-testid={`template-card-${tpl.slug}`}
              >
                <span className="template-industry">{tpl.industry}</span>
                <span className="template-name">{tpl.name}</span>
                <span className="template-desc">{tpl.description}</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                  {tpl.suggested_chips.slice(0, 4).map((c) => (
                    <span className="chip" key={c}>
                      {c}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </Drawer>

      <Modal
        title={editing ? "Edit prompt" : "New prompt"}
        open={!!editing || creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={saveMutation.isPending}
              onClick={form.handleSubmit((v) => saveMutation.mutate(v))}
              data-testid="submit-prompt"
            >
              {saveMutation.isPending ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Name</label>
          <input className="field-input" {...form.register("name")} data-testid="prompt-name" />
          <FormError message={form.formState.errors.name?.message} />
        </div>
        <div className="field-group">
          <label>System prompt</label>
          <textarea
            className="field-input mono"
            rows={12}
            style={{ fontSize: 12 }}
            data-testid="prompt-content"
            {...form.register("content")}
          />
          <div className="help">
            {contentLen.toLocaleString()} / {PROMPT_MAX.toLocaleString()} chars
          </div>
          <FormError message={form.formState.errors.content?.message} />
        </div>
        <div className="field-group">
          <label
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              {...form.register("is_active")}
              data-testid="prompt-active"
            />
            Set as the active prompt for this chatbot (other prompts will be
            deactivated automatically).
          </label>
        </div>
      </Modal>
    </div>
  );
}
